import React, { useEffect } from "react";
import { SliderMarks } from "antd/lib/slider";
import videos, { VideoJsPlayer } from "video.js";

import { useFullScreenChange } from "@Utils";
import { FallbackHandlersPlugin, IFallbackOptions } from "@Components/activity/FallbackHandlersPlugin";
import { SafariDocument } from "types";
import { IQualityLevels, qualityLevels } from "@Components/shared/QualityLevelsPlugin";

interface IProps {
    url?: string;
    marks?: SliderMarks;
    toggleFullScreen: () => Promise<void>;
    toggleFullScreenExit: () => void;
    onProgress?: (value: number) => void;
    onDuration?: (value: number) => void;
    options: videos.PlayerOptions;
}

interface VideoJsPlayerWithOurPlugins extends VideoJsPlayer {
    fallbackHandlersPlugin: (options?: IFallbackOptions) => void;
    qualityLevels: () => IQualityLevels;
}

// implement the contains a fallback for Safari on iOS ignoring out videojs events
videos.registerPlugin('fallbackHandlersPlugin', FallbackHandlersPlugin);

if (!videos.getPlugin("qualityLevels")) {
  // Register our custom quality levels plugin
  videos.registerPlugin("qualityLevelsPlugin", qualityLevels);
}

const IS_IOS_SAFARI = videos.browser.IS_SAFARI && videos.browser.IS_IOS;

interface VideoTimelineHook {
  (videoRef: React.RefObject<HTMLVideoElement>, playerRef: React.MutableRefObject<VideoJsPlayer>, props: IProps): {
    togglePlay: () => void;
    handleOnTimeUpdate: () => void;
    handleOnMetaLoaded: () => void;
    handleVideoProgress: (value: number) => void;
    handleVideoSpeed: (value: number) => void;
    handleVolume: (value: number) => void;
    toggleMute: () => void;
    setFullScreen: () => void;
    exitFullScreen: () => void;
  };
}

const VideoTimelineHooks: VideoTimelineHook = (videoRef, playerRef, props) => {

  const isFullScreen = !!document?.fullscreenElement || !!(document as SafariDocument)?.webkitCurrentFullScreenElement;

  const togglePlay = () => {
    !playerRef.current?.paused() ? playerRef.current?.pause() : playerRef.current?.play();
  };

  const handleOnTimeUpdate = () => {
    const progress = (playerRef.current.currentTime() / playerRef.current.duration()) * 100;
    props.onProgress(progress);
  };

  const handleOnMetaLoaded = () => {
      const playerDuration = playerRef?.current?.duration();
      const duration = playerDuration === Infinity ? 0 : playerDuration;
      props.onDuration(duration);
  };

  const handleVideoProgress = (value: number) => {
    const progress = Number(value);
    playerRef.current.currentTime((playerRef.current.duration() / 100) * progress);
    props.onProgress(progress);
  };

  const handleVideoSpeed = (value: number) => {
    const speed = value;
    playerRef.current.playbackRate(speed);
  };

  const toggleMute = () => {
    playerRef.current.muted(!playerRef.current.muted());
  };
  
  const handleVolume = (value: number) => {
    const volume = value;
    playerRef.current.volume(value);
    playerRef.current.muted(volume === 0);  
  };

  const setFullScreen = () => {
    props.toggleFullScreen();
  };

  const exitFullScreen = () => {
    props.toggleFullScreenExit();
    if (isFullScreen) {
      if (document.exitFullscreen) document.exitFullscreen();
      if ((document as SafariDocument)?.webkitExitFullscreen) (document as SafariDocument).webkitExitFullscreen();
    }
  };

  const isFullScreenChanged = useFullScreenChange();

  useEffect(() => {
    if (playerRef.current && !isFullScreenChanged) {
      props.toggleFullScreenExit();
    }
  }, [isFullScreenChanged]);

  useEffect(() => {
    playerRef.current = videos(videoRef.current, props.options);

    (playerRef.current as VideoJsPlayerWithOurPlugins).qualityLevels().on("addqualitylevel", ({ qualityLevel }) => {
        // disable representations with the 720 x 540 ratio
        if (qualityLevel.width === 720 && qualityLevel.height === 540) {
            return (qualityLevel.enabled = false);
        }
        return qualityLevel.enabled = true;
    });

    if (videos.browser.IS_SAFARI && videos.browser.IS_IOS) {
      (playerRef.current as VideoJsPlayerWithOurPlugins).fallbackHandlersPlugin({ handleOnMetaLoaded, handleOnTimeUpdate });
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
      }
    };
  }, [props.url]);

  return {
    togglePlay,
    handleOnTimeUpdate,
    handleOnMetaLoaded,
    handleVideoProgress,
    // iOS Safari does not support changing the playback rate with HTML video elements https://developer.apple.com/documentation/webkitjs/htmlmediaelement/1629746-playbackrate
    handleVideoSpeed: !IS_IOS_SAFARI ? handleVideoSpeed : null,
    handleVolume,
    toggleMute,
    setFullScreen,
    exitFullScreen,
  };
};

export default VideoTimelineHooks;
